import produce from 'immer';
import sortBy from 'lodash/sortBy';
import { useCallback, useMemo, useState } from 'react';
import { useDeepCompareEffect } from 'react-use';
import { analytics } from 'app/utils/segment';
import { createPrettyKeyCombination, isAppleDevice, keyCombinationsToString, } from 'utils/keyboard';
import { useServerStorage } from 'utils/useServerStorage';
import { commands, commandsKeyboardRebindable } from '../utils/commands';
/** Represents the user storage key for keybindings. */
const userStorageKey = isAppleDevice
    ? 'keybindings_mac'
    : 'keybindings_pc';
/** Represents a hook for working with commands. */
export function useCommands() {
    var _a, _b;
    const { data = {}, set, remove } = useServerStorage('user');
    const [userKeybindings, setUserKeybindings] = useState((_a = data[userStorageKey]) !== null && _a !== void 0 ? _a : {});
    const userKeybindingsData = (_b = data[userStorageKey]) !== null && _b !== void 0 ? _b : {};
    // Updates the user defined keybindings local state. We do this to ensure that
    // the consumers only re-render when keybindings update and not the rest of
    // the user storage.
    useDeepCompareEffect(() => {
        setUserKeybindings(userKeybindingsData);
    }, [userKeybindingsData]);
    /** Commands enriched with user defined keybindings. */
    const commandsWithUserKeybindings = useMemo(() => {
        const sortedCommands = sortBy(commands, ['group', 'label']);
        return sortedCommands.map((command) => {
            const commandUserKeybindings = userKeybindings[command.name];
            if (commandUserKeybindings) {
                return produce(command, (draft) => {
                    draft.keyboard = Object.assign(Object.assign({}, draft.keyboard), { keybindings: commandUserKeybindings });
                });
            }
            return command;
        });
    }, [userKeybindings]);
    /** Commands and their current keybindings. */
    const keybindings = useMemo(() => {
        return Object.fromEntries(commandsWithUserKeybindings.map((command) => {
            var _a, _b;
            return [
                command.name,
                (_b = (_a = command.keyboard) === null || _a === void 0 ? void 0 : _a.keybindings) !== null && _b !== void 0 ? _b : [],
            ];
        }));
    }, [commandsWithUserKeybindings]);
    const canResetAllRebindableKeybindings = useMemo(() => !!Object.keys(userKeybindings).length, [userKeybindings]);
    const canUnassignAllRebindableKeybindings = useMemo(() => commandsKeyboardRebindable.some(({ name }) => { var _a; return !(((_a = userKeybindings[name]) === null || _a === void 0 ? void 0 : _a.length) === 0); }), [userKeybindings]);
    const setKeybindings = useCallback((commandName, newKeybindings) => {
        if (!commandsKeyboardRebindable.some(({ name }) => name === commandName)) {
            console.warn(`Keybindings of '${commandName}' command can not be rebound`);
            return;
        }
        const oldKeybindings = keyCombinationsToString(keybindings[commandName]);
        set({
            [userStorageKey]: (draft) => {
                draft[commandName] = newKeybindings;
            },
        });
        analytics.settingsCommandsAssignKeybindings({
            commandName,
            oldKeybindings,
            newKeybindings: keyCombinationsToString(newKeybindings),
        });
    }, [keybindings, set]);
    const resetKeybindings = useCallback((commandName) => {
        if (!commandsKeyboardRebindable.some(({ name }) => name === commandName)) {
            console.warn(`Keybindings of '${commandName}' command can not be reset because it's not keyboard rebindable`);
            return;
        }
        set({
            [userStorageKey]: (draft) => {
                draft[commandName] = null;
            },
        });
        analytics.settingsCommandsResetKeybindings({ commandName });
    }, [set]);
    const unassignKeybindings = useCallback((commandName) => {
        if (!commandsKeyboardRebindable.some(({ name }) => name === commandName)) {
            console.warn(`Keybindings of '${commandName}' command can not be unassigned because it's not keyboard rebindable`);
            return;
        }
        set({
            [userStorageKey]: (draft) => {
                draft[commandName] = [];
            },
        });
        analytics.settingsCommandsUnassignKeybindings({ commandName });
    }, [set]);
    const unassignAllKeybindings = useCallback(() => {
        set({
            [userStorageKey]: (draft) => {
                commandsKeyboardRebindable.forEach(({ name }) => {
                    draft[name] = [];
                });
            },
        });
        analytics.settingsCommandsUnassignAllKeybindings();
    }, [set]);
    const resetAllUserKeybindings = useCallback(() => {
        remove([userStorageKey]);
        analytics.settingsCommandsResetAllKeybindings();
    }, [remove]);
    const isCommandKeyboardRebindable = useCallback((name) => {
        return !!commandsKeyboardRebindable.find((command) => command.name === name);
    }, []);
    const isUserDefinedCommandKeybindings = useCallback((name) => {
        return name in userKeybindings;
    }, [userKeybindings]);
    const hasCommandKeybindings = useCallback((name) => {
        var _a;
        return !!(((_a = keybindings[name]) === null || _a === void 0 ? void 0 : _a.length) > 0);
    }, [keybindings]);
    const enrichedCommands = useMemo(() => {
        const lookupItems = commandsWithUserKeybindings.reduce((lookupItems, command) => {
            var _a, _b;
            ((_b = (_a = command.keyboard) === null || _a === void 0 ? void 0 : _a.keybindings) !== null && _b !== void 0 ? _b : []).forEach((keybinding) => {
                const keybindingsString = createPrettyKeyCombination(keybinding, {
                    preferBinding: true,
                }).toString();
                const lookupItem = lookupItems.find(([keybindings]) => keybindings === keybindingsString);
                const seenCommand = {
                    [command.name]: {
                        command,
                        isUserDefinedKeybindings: isUserDefinedCommandKeybindings(command.name),
                    },
                };
                if (lookupItem) {
                    lookupItem[1] = Object.assign(lookupItem[1], seenCommand);
                }
                else {
                    lookupItems.push([
                        keybindingsString,
                        seenCommand,
                    ]);
                }
            });
            return lookupItems;
        }, []);
        const commandConflicts = commandsWithUserKeybindings.reduce((acc, command) => {
            const lookupItem = lookupItems.find(([_, seenCommands]) => seenCommands[command.name]);
            if (lookupItem) {
                acc[command.name] = Object.fromEntries(Object.entries(lookupItem[1]).filter(([name]) => command.name !== name));
            }
            return acc;
        }, {});
        return commandsWithUserKeybindings.map((command) => {
            const { name } = command;
            const conflict = commandConflicts[name];
            const hasKeybindings = hasCommandKeybindings(name);
            const isKeyboardRebindable = isCommandKeyboardRebindable(name);
            const isUserDefinedKeybindings = isUserDefinedCommandKeybindings(name);
            const canResetKeybindings = isKeyboardRebindable && isUserDefinedKeybindings;
            const canUnassignKeybindings = isKeyboardRebindable && hasKeybindings;
            return {
                command,
                conflicts: conflict
                    ? Object.entries(conflict).map(([_, command]) => command)
                    : [],
                isUserDefinedKeybindings,
                canResetKeybindings,
                canUnassignKeybindings,
                hasKeybindings,
                isKeyboardRebindable,
            };
        });
    }, [
        hasCommandKeybindings,
        isCommandKeyboardRebindable,
        isUserDefinedCommandKeybindings,
        commandsWithUserKeybindings,
    ]);
    const hasKeybindingsConflicts = useMemo(() => enrichedCommands.some(({ conflicts }) => conflicts.length > 0), [enrichedCommands]);
    const getKeybindings = useCallback(function (commandNames) {
        return Object.fromEntries(Object.entries(keybindings).filter(([commandName]) => commandNames.includes(commandName)));
    }, [keybindings]);
    return {
        enrichedCommands,
        keybindings,
        canResetAllRebindableKeybindings,
        canUnassignAllRebindableKeybindings,
        hasKeybindingsConflicts,
        setKeybindings,
        getKeybindings,
        resetKeybindings,
        unassignKeybindings,
        resetAllUserKeybindings,
        unassignAllKeybindings,
    };
}
